import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface GraphicNewsInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const GraphicNewsInsights: React.FC<GraphicNewsInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Identify active bullet
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Snappy spring entrance animations
  const textOpacity = interpolate(elapsedFrames, [0, 5], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.94,
    to: 1,
    config: { damping: 11, stiffness: 110, mass: 0.6 },
  });

  // Card entrance
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 12, stiffness: 95, mass: 0.7 },
  });

  // Bullet duration progress
  const bulletDurationMs = activeBullet.endMs - activeBullet.startMs;
  const elapsedMs = Math.max(0, currentMs - activeBullet.startMs);
  const progressPct = Math.min(100, (elapsedMs / bulletDurationMs) * 100);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
        opacity: cardOpacity,
        transform: `scale(${cardScale})`,
      }}
    >
      {/* Slanted Card Container */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          transform: "skewX(-8deg) rotate(-2deg)",
          position: "relative",
        }}
      >
        {/* Decorative Grid Patterns Backing Card */}
        <div
          style={{
            position: "absolute",
            top: "-40px",
            right: "-20px",
            width: "80px",
            height: "80px",
            backgroundImage: "radial-gradient(rgba(0,0,0,0.15) 20%, transparent 20%)",
            backgroundSize: "10px 10px",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-40px",
            left: "-25px",
            width: "100px",
            height: "100px",
            backgroundImage: "repeating-linear-gradient(45deg, rgba(225,29,72,0.1) 0px, rgba(225,29,72,0.1) 4px, transparent 4px, transparent 8px)",
            zIndex: 0,
          }}
        />

        {/* Top Slanted Card Tab (Volt Yellow / Pink Accent) */}
        <div
          style={{
            background: "#FFE600",
            border: "3.5px solid #000000",
            padding: "6px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "80%",
            zIndex: 3,
            boxShadow: "3px 3px 0px #000000",
          }}
        >
          <span
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 900,
              fontSize: "14px",
              color: "#000000",
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            INSIGHT 0{activeIndex + 1}
          </span>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#000000"
                accentColor="#FFE600"
                size={54}
                style="badge"
                opacity={textOpacity}
                containerStyle={{
                  border: "2px solid #000000",
                  boxShadow: "2px 2px 0px #000000",
                  background: "#FFE600",
                  transform: "skewX(8deg)",
                }}
              />
            ) : null;
          })()}
        </div>

        {/* Content Card Body */}
        <div
          style={{
            width: "100%",
            background: "#FFFFFF",
            border: "4px solid #000000",
            boxShadow: "8px 8px 0px #000000",
            padding: "40px 30px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            zIndex: 2,
            marginTop: "-6px",
          }}
        >
          {/* Bullet Text */}
          <div
            style={{
              opacity: textOpacity,
              transform: `scale(${textScale})`,
              minHeight: "260px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <p
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "88px",
                fontWeight: 900,
                lineHeight: 1.0,
                color: "#111625",
                textAlign: "left",
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              <HighlightText
                highlightStyle={{
                  fontWeight: 900,
                  background: "#FFE600",
                  color: "#000000",
                  padding: "4px 10px",
                  display: "inline",
                  border: "2px solid #000000",
                  boxShadow: "2px 2px 0px #000000",
                }}
                text={activeBullet.text}
                highlights={activeBullet.highlights}
              />
            </p>
          </div>

          {/* Slanted progress line bar */}
          <div
            style={{
              width: "100%",
              height: "8px",
              background: "#E4E4E7",
              border: "2px solid #000000",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: "100%",
                background: "#E11D48",
                transition: "width 0.05s linear",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
